import { getOpenBlockers } from '@/lib/issue-links.js';
import { useParams, Link } from 'react-router-dom';
import { useState, useMemo, useCallback } from 'react';
import { DndContext, DragOverlay, useDroppable, useDraggable, closestCenter, MouseSensor, TouchSensor, useSensors, useSensor } from '@dnd-kit/core';
import type { DragStartEvent, DragEndEvent } from '@dnd-kit/core';
import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api.js';
import { useBoardConfig, useBoardIssues, useKanbanBacklog, useBoard } from '@/hooks/useBoards.js';
import { IssueFilters, extractFilterOptions } from '@/components/IssueFilters.js';
import { useUrlFilterState, useUrlTextFilter } from '@/hooks/useUrlFilterState.js';
import { IssueCard } from './IssueCard.js';
import { SearchInput } from '@/components/SearchInput.js';
import { PageHeader } from '@/components/PageHeader.js';
import { LoadingState, EmptyState } from '@/components/PageState.js';
import type { JiraIssueSummary } from '@shared/types.js';

// Board hides Cancelled/Closed columns — Done stays visible as the end state
const BOARD_HIDDEN_COLUMNS = new Set(['cancelled', 'canceled', 'closed']);
const DONE_STATUSES = new Set(['done', 'completed']);
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
const BACKLOG_COL = 'Backlog';

// ── Draggable Issue Card ─────────────────────────────────────────────

function DraggableIssueCard({ issue }: { issue: JiraIssueSummary }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: issue.key,
    data: { issue },
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={isDragging ? 'opacity-30' : ''}
    >
      <IssueCard issue={issue} />
    </div>
  );
}

// ── Droppable Column ─────────────────────────────────────────────────

function DroppableColumn({ name, displayName, issues, isOver, maxHeight, readOnly }: {
  name: string;
  displayName: string;
  issues: JiraIssueSummary[];
  isOver: boolean;
  maxHeight?: string;
  readOnly?: boolean;
}) {
  const { setNodeRef } = useDroppable({ id: `column:${name}`, disabled: readOnly });

  return (
    <div
      ref={setNodeRef}
      className={`flex w-[272px] min-w-[240px] flex-1 flex-col rounded-lg p-2 transition-colors ${
        isOver && !readOnly ? 'bg-blue-50 ring-2 ring-blue-300' : 'bg-gray-100'
      }`}
    >
      <div className="mb-2 flex items-center gap-2 px-1.5 pt-1">
        <h3 className="truncate text-xs font-semibold uppercase tracking-wide text-gray-600">{displayName}</h3>
        <span className="text-xs font-medium text-gray-500">{issues.length}</span>
      </div>
      <div
        className="flex flex-col gap-2 overflow-y-auto"
        style={maxHeight ? { maxHeight } : undefined}
      >
        {issues.length === 0 ? (
          <div className="rounded-md border border-dashed border-gray-300 py-6 text-center text-xs text-gray-400">No issues</div>
        ) : (
          issues.map((issue) => (
            <DraggableIssueCard key={issue.key} issue={issue} />
          ))
        )}
      </div>
    </div>
  );
}

// ── Board View ───────────────────────────────────────────────────────

export function BoardView() {
  const { id } = useParams<{ id: string }>();
  const boardId = id ? parseInt(id, 10) : null;
  const [activeIssue, setActiveIssue] = useState<JiraIssueSummary | null>(null);
  const [overColumn, setOverColumn] = useState<string | null>(null);
  const mouseSensor = useSensor(MouseSensor, { activationConstraint: { distance: 10 } });
  const touchSensor = useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } });
  const sensors = useSensors(mouseSensor, touchSensor);

  const board = useBoard(boardId);
  const { data: config, isLoading: configLoading } = useBoardConfig(boardId);
  // A card move in flight (its requests, then its transition) holds the board's automatic re-reads.
  // Both are mutations under one key, so the hold survives leaving and reopening the board.
  const movePending = useIsMutating({ mutationKey: ['board-move', boardId] }) > 0;
  const { data: boardData, isLoading: issuesLoading, isFetching: issuesFetching } = useBoardIssues(boardId, movePending);
  const { data: kanbanData, isLoading: backlogLoading, isFetching: backlogFetching, isError: backlogError, refetch: retryBacklog } = useKanbanBacklog(boardId, movePending);
  const { filters, setFilters } = useUrlFilterState('updated', 'DESC');
  const [searchText, setSearchText] = useUrlTextFilter('q');
  const queryClient = useQueryClient();

  // Transition mutation for drag-and-drop
  const moveMutation = useMutation({
    mutationKey: ['board-move', boardId],
    mutationFn: (move: () => Promise<void>) => move(),
  });

  const transitionMutation = useMutation({
    mutationKey: ['board-move', boardId],
    mutationFn: ({ issueKey, transitionId }: { issueKey: string; transitionId: string }) =>
      api.post(`/api/issues/${issueKey}/transitions`, { transitionId }),
    onSettled: (_data, _err, { issueKey }) => {
      void queryClient.invalidateQueries({ queryKey: ['board', boardId, 'issues'] });
      void queryClient.invalidateQueries({ queryKey: ['board', boardId, 'kanban-backlog'] });
      void queryClient.invalidateQueries({ queryKey: ['issue', issueKey] });
    },
  });

  // Greenhopper divider: boardIssueIds tells us which issues are on the board vs backlog
  const INACTIVE_STATUSES = useMemo(() => new Set(['done', 'completed', 'cancelled', 'canceled', 'closed']), []);

  // Build a map of issue id → key from Greenhopper data
  const kanbanIssueMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const i of kanbanData?.issues ?? []) {
      map.set(i.key, i.id);
    }
    return map;
  }, [kanbanData?.issues]);

  const greenHopperBoardIds = useMemo(
    () => new Set(kanbanData?.boardIssueIds ?? []),
    [kanbanData?.boardIssueIds],
  );

  // Backlog issue keys = issues in Greenhopper data that are NOT in boardIssueIds and are active
  const backlogIssueKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const i of kanbanData?.issues ?? []) {
      if (!greenHopperBoardIds.has(i.id) && !i.done) {
        keys.add(i.key);
      }
    }
    return keys;
  }, [kanbanData?.issues, greenHopperBoardIds]);

  // Board issues from the agile API, excluding backlog items
  const boardIssues = useMemo(() =>
    ((boardData?.issues ?? []) as JiraIssueSummary[]).filter((i) => !backlogIssueKeys.has(i.key)),
  [boardData?.issues, backlogIssueKeys]);

  // Backlog issues: from board API data, filtered to only backlog keys
  // (board API returns all issues including backlog; we use the divider to separate)
  const backlogIssues = useMemo(() =>
    ((boardData?.issues ?? []) as JiraIssueSummary[]).filter(
      (i) => backlogIssueKeys.has(i.key) && !INACTIVE_STATUSES.has(i.status.name.toLowerCase()),
    ),
  [boardData?.issues, backlogIssueKeys, INACTIVE_STATUSES]);

  // All issues for filter option extraction
  const allIssues = useMemo(() => [...boardIssues, ...backlogIssues], [boardIssues, backlogIssues]);

  const { statuses, assignees } = useMemo(() => extractFilterOptions(allIssues), [allIssues]);

  const handleFilterChange = useCallback((newFilters: typeof filters) => {
    setFilters(newFilters);
  }, [setFilters]);

  // Apply text search + status/assignee filters
  const filterIssue = useCallback((issue: JiraIssueSummary): boolean => {
    const statusLower = issue.status.name.toLowerCase();

    // Always hide Cancelled/Closed
    if (!filters.showDone && BOARD_HIDDEN_COLUMNS.has(statusLower)) {
      return false;
    }
    // Done issues: only show if updated within last 7 days
    if (DONE_STATUSES.has(statusLower)) {
      const updatedAt = issue.updated ? new Date(issue.updated).getTime() : 0;
      if (Date.now() - updatedAt > SEVEN_DAYS_MS) {
        return false;
      }
    }
    if (filters.blockedOnly && getOpenBlockers(issue.links).length === 0) {
      return false;
    }
    if (filters.statuses.length > 0 && !filters.statuses.includes(issue.status.name)) {
      return false;
    }
    if (filters.assignees.length > 0) {
      const hasUnassigned = filters.assignees.includes('UNASSIGNED');
      const namedFilters = filters.assignees.filter((a) => a !== 'UNASSIGNED');
      const assigneeName = issue.assignee?.displayName;
      if (!assigneeName) {
        if (!hasUnassigned) return false;
      } else if (!namedFilters.includes(assigneeName)) {
        return false;
      }
    }
    // Text search
    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      if (
        !issue.summary?.toLowerCase().includes(lower) &&
        !issue.key.toLowerCase().includes(lower) &&
        !issue.assignee?.displayName?.toLowerCase().includes(lower)
      ) {
        return false;
      }
    }
    return true;
  }, [filters, searchText]);

  // Sort function for issues within columns
  const sortFn = useCallback((a: JiraIssueSummary, b: JiraIssueSummary): number => {
    const dir = filters.sortDir === 'ASC' ? 1 : -1;
    const field = filters.sortField;
    let aVal: string | number = '';
    let bVal: string | number = '';

    if (field === 'priority') {
      const priorityOrder: Record<string, number> = { Highest: 0, High: 1, Medium: 2, Low: 3, Lowest: 4 };
      aVal = priorityOrder[a.priority?.name] ?? 5;
      bVal = priorityOrder[b.priority?.name] ?? 5;
    } else if (field === 'assignee') {
      aVal = a.assignee?.displayName || 'zzz';
      bVal = b.assignee?.displayName || 'zzz';
    } else if (field === 'summary') {
      aVal = a.summary?.toLowerCase() || '';
      bVal = b.summary?.toLowerCase() || '';
    } else if (field === 'key') {
      const aNum = parseInt(a.key.split('-')[1] || '0', 10);
      const bNum = parseInt(b.key.split('-')[1] || '0', 10);
      return (aNum - bNum) * dir;
    } else if (field === 'created') {
      aVal = a.created || '';
      bVal = b.created || '';
    } else {
      aVal = a.updated || '';
      bVal = b.updated || '';
    }

    if (aVal < bVal) return -1 * dir;
    if (aVal > bVal) return 1 * dir;
    return 0;
  }, [filters.sortField, filters.sortDir]);

  // Build columns: Backlog first, then board columns
  const columns = useMemo(() => {
    if (!config?.columns) return [];

    const statusToColumn = new Map<string, string>();
    for (const col of config.columns) {
      for (const sid of col.statusIds) {
        statusToColumn.set(sid, col.name);
      }
    }
    const statusNameToColumn = new Map<string, string>();
    for (const col of config.columns) {
      statusNameToColumn.set(col.name.toLowerCase(), col.name);
    }

    // Group board issues into columns
    const groups = new Map<string, JiraIssueSummary[]>();
    groups.set(BACKLOG_COL, []);
    for (const col of config.columns) {
      groups.set(col.name, []);
    }

    // Add board issues to their columns, track which keys are placed
    const placedKeys = new Set<string>();
    for (const issue of boardIssues.filter(filterIssue)) {
      const iss = issue as JiraIssueSummary & { statusId?: string };
      let colName = iss.statusId ? statusToColumn.get(iss.statusId) : undefined;
      if (!colName) colName = statusNameToColumn.get(iss.status.name.toLowerCase());
      if (!colName) colName = config.columns[0]?.name || 'Unknown';
      const list = groups.get(colName);
      if (list) list.push(issue);
      else groups.set(colName, [issue]);
      placedKeys.add(issue.key);
    }

    // Add backlog issues to the Backlog column (skip any already in a board column)
    groups.set(BACKLOG_COL, backlogIssues.filter((i) => !placedKeys.has(i.key)).filter(filterIssue));

    // Build final columns array: Backlog first, then board columns
    const result: Array<{ name: string; displayName: string; issues: JiraIssueSummary[] }> = [];

    result.push({
      name: BACKLOG_COL,
      displayName: `Backlog`,
      issues: [...(groups.get(BACKLOG_COL) || [])].sort(sortFn),
    });

    for (const col of config.columns) {
      if (!filters.showDone && BOARD_HIDDEN_COLUMNS.has(col.name.toLowerCase())) continue;
      const displayName = DONE_STATUSES.has(col.name.toLowerCase()) ? `${col.name} (Last 7 Days)` : col.name;
      result.push({
        name: col.name,
        displayName,
        issues: [...(groups.get(col.name) || [])].sort(sortFn),
      });
    }

    return result;
  }, [config, boardIssues, backlogIssues, filterIssue, sortFn, filters.showDone]);

  // DnD handlers
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const issue = event.active.data.current?.issue as JiraIssueSummary;
    setActiveIssue(issue || null);
  }, []);

  const handleDragOver = useCallback((event: { over: { id: string | number } | null }) => {
    const overId = event.over?.id as string | null;
    if (overId?.startsWith('column:')) {
      setOverColumn(overId.replace('column:', ''));
    } else {
      setOverColumn(null);
    }
  }, []);


  const handleDragAsync = useCallback(async (
    issueKey: string,
    issue: JiraIssueSummary,
    targetColumn: string,
    isFromBacklog: boolean,
  ) => {
    // Optimistically move the card
    const targetStatusId = config?.columns.find(
      (col) => col.name.toLowerCase() === targetColumn.toLowerCase(),
    )?.statusIds[0] || null;

    const boardQueryKey = ['board', boardId, 'issues'];
    const backlogQueryKey = ['board', boardId, 'kanban-backlog'];

    await moveMutation.mutateAsync(moveCard);

    async function moveCard(): Promise<void> {
      await queryClient.cancelQueries({ queryKey: boardQueryKey });
      await queryClient.cancelQueries({ queryKey: backlogQueryKey });

      const previousBoardData = queryClient.getQueryData(boardQueryKey);
      const previousBacklogData = queryClient.getQueryData(backlogQueryKey);

      const revert = () => {
        queryClient.setQueryData(boardQueryKey, previousBoardData);
        queryClient.setQueryData(backlogQueryKey, previousBacklogData);
      };

      // Optimistic UI update — modify the Greenhopper kanban data to move the divider
      const issueInternalId = kanbanIssueMap.get(issueKey);

      if (targetColumn === BACKLOG_COL) {
        // Board → Backlog: remove issue from boardIssueIds
        if (issueInternalId != null) {
          queryClient.setQueryData(backlogQueryKey, (old: unknown) => {
            if (!old || typeof old !== 'object') return old;
            const data = old as { boardIssueIds: number[]; issues: unknown[] };
            return { ...data, boardIssueIds: data.boardIssueIds.filter((id) => id !== issueInternalId) };
          });
        }
      } else if (isFromBacklog) {
        // Backlog → Board: add issue to boardIssueIds + update status if needed
        if (issueInternalId != null) {
          queryClient.setQueryData(backlogQueryKey, (old: unknown) => {
            if (!old || typeof old !== 'object') return old;
            const data = old as { boardIssueIds: number[]; issues: unknown[] };
            return { ...data, boardIssueIds: [...data.boardIssueIds, issueInternalId] };
          });
        }
        if (issue.status.name.toLowerCase() !== targetColumn.toLowerCase()) {
          queryClient.setQueryData(boardQueryKey, (old: unknown) => {
            if (!old || typeof old !== 'object') return old;
            const data = old as { issues: Array<JiraIssueSummary & { statusId?: string | null }>; total: number };
            return {
              ...data,
              issues: data.issues.map((iss) =>
                iss.key === issueKey
                  ? { ...iss, status: { ...iss.status, name: targetColumn }, statusId: targetStatusId }
                  : iss,
              ),
            };
          });
        }
      } else {
        // Board column → Board column: update status
        queryClient.setQueryData(boardQueryKey, (old: unknown) => {
          if (!old || typeof old !== 'object') return old;
          const data = old as { issues: Array<JiraIssueSummary & { statusId?: string | null }>; total: number };
          return {
            ...data,
            issues: data.issues.map((iss) =>
              iss.key === issueKey
                ? { ...iss, status: { ...iss.status, name: targetColumn }, statusId: targetStatusId }
                : iss,
            ),
          };
        });
      }

      // Execute the actual API calls
      try {
        if (targetColumn === BACKLOG_COL) {
          // Board → Backlog via Kanban backlog API
          const res = await fetch(`/api/boards/${boardId}/move-to-backlog`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ issueKeys: [issueKey] }),
          });
          if (!res.ok) throw new Error('Failed to move to backlog');
          void queryClient.invalidateQueries({ queryKey: boardQueryKey });
          void queryClient.invalidateQueries({ queryKey: backlogQueryKey });

        } else if (isFromBacklog) {
          // Backlog → Board: move to board, then transition status if needed
          const moveRes = await fetch(`/api/boards/${boardId}/move-to-board`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ issueKeys: [issueKey] }),
          });
          if (!moveRes.ok) throw new Error('Failed to move to board');

          if (issue.status.name.toLowerCase() !== targetColumn.toLowerCase()) {
            const res = await fetch(`/api/issues/${issueKey}`, { credentials: 'include' });
            if (res.ok) {
              const body = await res.json() as { data: { transitions: Array<{ id: string; name: string; to: { name: string } }> } };
              const transition = body.data.transitions?.find(
                (t: { name: string; to: { name: string } }) =>
                  t.name.toLowerCase() === targetColumn.toLowerCase() ||
                  t.to?.name?.toLowerCase() === targetColumn.toLowerCase(),
              );
              if (transition) {
                transitionMutation.mutate({ issueKey, transitionId: transition.id }, { onError: revert });
                return;
              }
            }
          }
          void queryClient.invalidateQueries({ queryKey: boardQueryKey });
          void queryClient.invalidateQueries({ queryKey: backlogQueryKey });

        } else {
          // Board → Board: status transition
          const res = await fetch(`/api/issues/${issueKey}`, { credentials: 'include' });
          if (!res.ok) throw new Error('Failed to fetch transitions');
          const body = await res.json() as { data: { transitions: Array<{ id: string; name: string; to: { name: string } }> } };
          const transition = body.data.transitions?.find(
            (t: { name: string; to: { name: string } }) =>
              t.name.toLowerCase() === targetColumn.toLowerCase() ||
              t.to?.name?.toLowerCase() === targetColumn.toLowerCase(),
          );
          if (transition) {
            transitionMutation.mutate({ issueKey, transitionId: transition.id }, { onError: revert });
          } else {
            revert();
          }
        }
      } catch (err) {
        console.error('Drag operation failed:', err);
        revert();
      }
    }
  }, [moveMutation, transitionMutation, boardId, queryClient, config, kanbanIssueMap]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveIssue(null);
    setOverColumn(null);

    const issueKey = event.active.id as string;
    const overId = event.over?.id as string | undefined;
    if (!overId?.startsWith('column:')) return;

    const targetColumn = overId.replace('column:', '');
    const issue = event.active.data.current?.issue as JiraIssueSummary;
    if (!issue) return;

    // Determine current column
    const isFromBacklog = backlogIssueKeys.has(issueKey);
    // The column the card is rendered in — a column can hold several statuses,
    // so comparing status names would treat a same-column drop as a move.
    const currentColumn = columns.find((col) => col.issues.some((i) => i.key === issueKey))?.name
      ?? (isFromBacklog ? BACKLOG_COL : issue.status.name);
    if (currentColumn.toLowerCase() === targetColumn.toLowerCase()) return;

    // Run the async work in a separate function to avoid silent failures
    void handleDragAsync(issueKey, issue, targetColumn, isFromBacklog);
  }, [backlogIssueKeys, columns, handleDragAsync]);

  const isLoading = configLoading || issuesLoading || backlogLoading;
  const isFetching = issuesFetching || backlogFetching;
  const visibleCount = columns.reduce((sum, col) => sum + col.issues.length, 0);

  return (
    <div>
      <PageHeader
        breadcrumb={<Link to="/boards" className="hover:text-blue-700 hover:underline">Boards</Link>}
        title={board?.name || `Board ${id}`}
        meta={boardData ? (
          <span className="inline-flex items-center gap-2">
            {visibleCount} issues
            {(isFetching || transitionMutation.isPending) && (
              <svg className="h-4 w-4 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24" aria-label="Refreshing">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
          </span>
        ) : undefined}
        actions={<SearchInput value={searchText} onChange={setSearchText} placeholder="Search this board..." className="w-full sm:w-72" />}
      />

      <IssueFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        statuses={statuses}
        assignees={assignees}
      />

      {backlogError ? (
        <div role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          Board/backlog membership is unavailable. Cards cannot be separated safely.
          <button className="ml-2 font-semibold underline" onClick={() => { void retryBacklog(); }}>Retry</button>
        </div>
      ) : isLoading ? (
        <LoadingState label="Loading board..." />
      ) : columns.length === 0 ? (
        <EmptyState title="No columns configured" />
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div className="flex gap-3 overflow-x-auto pb-4">
            {columns.map((col) => (
              <DroppableColumn
                key={col.name}
                name={col.name}
                displayName={col.displayName}
                issues={col.issues}
                isOver={overColumn === col.name}
                maxHeight="80vh"
                readOnly={false}
              />
            ))}
          </div>

          <DragOverlay dropAnimation={null}>
            {activeIssue && (
              <div className="w-64 rotate-2 opacity-95 shadow-lg">
                <IssueCard issue={activeIssue} />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
}
